import { useState, useRef, useEffect } from "react";
import { useToast } from "@/components/base/Toast";
import { fetchApi } from "@/services/apiService";
import type { WhatsAppConversation } from "./ConversationList";
import CallModal from "@/components/feature/CallModal";

export interface AgentItem {
  id: string;
  name: string;
  initials: string;
  role?: string;
  openChats?: number;
  active?: boolean;
  avatarBg?: string;
  avatarTextColor?: string;
}

interface Props {
  conversation: WhatsAppConversation;
  onToggleLeadPanel: () => void;
  leadPanelOpen: boolean;
  onSendMessage: (text: string) => void;
  onAssignAgent: (convId: number | string, agent: AgentItem) => void;
  onReassignAgent: (convId: number | string, fromAgent: string, toAgent: AgentItem, reason: string) => void;
  onMarkResolved: (convId: number | string) => void;
  agentsList?: AgentItem[];
  onNavigateToLead?: () => void;
}

export default function ChatWindow({
  conversation,
  onToggleLeadPanel,
  leadPanelOpen,
  onSendMessage,
  onAssignAgent,
  onReassignAgent,
  onMarkResolved,
  agentsList = [],
  onNavigateToLead,
}: Props) {
  const { showToast } = useToast();
  const [input, setInput] = useState("");
  const [showMenu, setShowMenu] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showAgentPicker, setShowAgentPicker] = useState(false);
  const [agentPickerMode, setAgentPickerMode] = useState<"assign" | "reassign">("assign");
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignAgentId, setReassignAgentId] = useState("");
  const [reassignReason, setReassignReason] = useState("");
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [quickReplies, setQuickReplies] = useState<{ label: string; text: string }[]>([]);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const isUnassigned = !conversation.assignedAgentName || conversation.assignedAgentName === "Unassigned";

  // Fetch quick replies & templates
  useEffect(() => {
    fetchApi<any[]>('/whatsapp/quick-replies', undefined, [])
      .then((res) => {
        const items = Array.isArray(res) ? res : (res as any)?.data || [];
        if (items.length > 0) {
          setQuickReplies(
            items.map((r: any) => ({
              label: r.title || r.shortcut || "Reply",
              text: r.message || "",
            }))
          );
        } else {
          setQuickReplies([
            { label: "Greeting", text: "Hello! Thank you for contacting Digi India. How can I help you today?" },
            { label: "Send Brochure", text: "Here is the project brochure with full floor plans and pricing details." },
            { label: "Site Visit", text: "Would you like to schedule a site visit this weekend?" },
            { label: "Pricing", text: "Here is the detailed quotation and payment plan for your review." },
            { label: "Follow-up", text: "Hi, just checking in to see if you have any questions about the proposal." }
          ]);
        }
      })
      .catch(() => {
        setQuickReplies([
          { label: "Greeting", text: "Hello! Thank you for contacting Digi India. How can I help you today?" },
          { label: "Send Brochure", text: "Here is the project brochure with full floor plans and pricing details." },
          { label: "Site Visit", text: "Would you like to schedule a site visit this weekend?" },
          { label: "Pricing", text: "Here is the detailed quotation and payment plan for your review." },
          { label: "Follow-up", text: "Hi, just checking in to see if you have any questions about the proposal." }
        ]);
      });
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversation.id, conversation.messages]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    // Send directly in-place from CRM without opening external tab or app
    onSendMessage(trimmed);
    setInput("");
    setShowTemplates(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTemplateClick = (text: string) => {
    setInput(text);
    setShowTemplates(false);
  };

  const handlePhoneClick = () => {
    if (!conversation.phone) {
      showToast("No phone number available for this contact", "warning");
      return;
    }
    setCallModalOpen(true);
  };

  const handleOpenWhatsApp = () => {
    if (!conversation.phone) {
      showToast("No phone number available for this lead", "error");
      return;
    }
    const success = openWhatsAppChat(conversation.phone, input.trim() || undefined);
    if (success) {
      showToast(`Opening WhatsApp chat with ${conversation.name}`, "success");
    } else {
      showToast("Unable to open WhatsApp for this number", "error");
    }
  };

  const handleAssignClick = () => {
    setShowMenu(false);
    setAgentPickerMode("assign");
    setShowAgentPicker(true);
  };

  const handleReassignClick = () => {
    setShowMenu(false);
    setShowReassignModal(true);
    setReassignAgentId("");
    setReassignReason("");
  };

  const handleAgentSelect = (agent: AgentItem) => {
    if (agentPickerMode === "assign") {
      onAssignAgent(conversation.id, agent);
      showToast(`Chat assigned to ${agent.name}`, "success");
    }
    setShowAgentPicker(false);
  };

  const handleReassignConfirm = () => {
    if (!reassignAgentId) return;
    const toAgent = agentsList.find((a) => String(a.id) === String(reassignAgentId) || a.name === reassignAgentId);
    if (!toAgent) return;
    onReassignAgent(conversation.id, conversation.assignedAgentName || "Unassigned", toAgent, reassignReason);
    showToast(`Chat reassigned to ${toAgent.name}`, "success");
    setShowReassignModal(false);
  };

  const messages = conversation.messages || [];
  const contactInitials = conversation.name
    ? conversation.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : "C";

  return (
    <div className="flex-1 flex flex-col min-w-0 bg-gray-50">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#e5e7eb] bg-white">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-full bg-indigo-50 text-indigo-700 flex items-center justify-center flex-shrink-0 font-semibold text-sm">
            <span>{contactInitials}</span>
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-[#111827] flex items-center gap-2">
              <span className="truncate">{conversation.name}</span>
              {conversation.company && (
                <span className="text-xs font-normal text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded truncate max-w-[140px]">
                  {conversation.company}
                </span>
              )}
            </h3>
            <div className="flex items-center gap-2 text-xs text-[#6b7280] mt-0.5">
              <span>Lead &middot; {conversation.stage || "New Inquiry"}</span>
              <span>&bull;</span>
              <button
                onClick={handlePhoneClick}
                title="Click to place direct call"
                className="text-emerald-700 font-semibold hover:underline flex items-center gap-1 cursor-pointer"
              >
                <i className="ri-phone-fill text-emerald-600 text-xs" />
                <span>{conversation.phone || "No phone"}</span>
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          <button
            onClick={handlePhoneClick}
            title="Direct Call Lead in CRM"
            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white transition-colors flex items-center gap-1.5 cursor-pointer shadow-sm active:scale-98"
          >
            <i className="ri-phone-fill text-sm" />
            <span>Call</span>
          </button>
          <button
            onClick={onToggleLeadPanel}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border whitespace-nowrap transition-colors cursor-pointer ${
              leadPanelOpen
                ? "bg-indigo-600 text-white border-indigo-600"
                : "text-indigo-600 border-indigo-600 hover:bg-indigo-50"
            }`}
          >
            <i className="ri-user-search-line mr-1" />
            View Lead
          </button>
          {onNavigateToLead && (
            <button
              onClick={onNavigateToLead}
              title="Open full Lead Profile"
              className="px-2.5 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-1 cursor-pointer"
            >
              <i className="ri-external-link-line" />
              <span className="hidden sm:inline">Profile</span>
            </button>
          )}

          {/* 3-dot menu */}
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            >
              <i className="ri-more-2-fill text-base" />
            </button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-xl border border-[#e5e7eb] shadow-lg z-20 py-1 animate-slide-in">
                  <button
                    onClick={() => { onMarkResolved(conversation.id); showToast("Conversation marked as resolved", "success"); setShowMenu(false); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-[#374151] hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-check-line mr-2 text-green-600 w-4 h-4 inline-flex items-center justify-center" /> Mark Resolved
                  </button>
                  {isUnassigned ? (
                    <button
                      onClick={handleAssignClick}
                      className="w-full text-left px-4 py-2.5 text-sm text-[#374151] hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-user-add-line mr-2 text-indigo-600 w-4 h-4 inline-flex items-center justify-center" /> Assign to Agent
                    </button>
                  ) : (
                    <button
                      onClick={handleReassignClick}
                      className="w-full text-left px-4 py-2.5 text-sm text-[#374151] hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-user-received-line mr-2 text-indigo-600 w-4 h-4 inline-flex items-center justify-center" /> Reassign Chat
                    </button>
                  )}
                  <button
                    onClick={() => { showToast("Note added to conversation", "success"); setShowMenu(false); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-[#374151] hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-sticky-note-line mr-2 text-amber-600 w-4 h-4 inline-flex items-center justify-center" /> Add Note
                  </button>
                  <button
                    onClick={() => { showToast(`${conversation.name} blocked`, "error"); setShowMenu(false); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-forbid-line mr-2 w-4 h-4 inline-flex items-center justify-center" /> Block
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Reassignment history banner */}
      {conversation.reassignedFrom && (
        <div className="bg-indigo-50 border-b border-indigo-100 px-4 py-1.5 flex items-center gap-1.5">
          <i className="ri-arrow-left-right-line text-indigo-500 text-xs w-4 h-4 flex items-center justify-center" />
          <p className="text-xs text-indigo-700">
            Reassigned from <strong>{conversation.reassignedFrom}</strong>
            {conversation.reassignReason && <> — {conversation.reassignReason}</>}
          </p>
        </div>
      )}

      {/* Chat area */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {/* Today separator */}
        <div className="flex justify-center">
          <span className="bg-gray-200 text-gray-600 text-xs rounded-full px-3 py-1 font-medium">Today</span>
        </div>

        {messages.map((msg) => {
          const isIncoming = msg.direction === "incoming";
          return (
            <div
              key={msg.id}
              className={`flex ${isIncoming ? "justify-start" : "justify-end"}`}
            >
              <div
                className={`max-w-xs px-4 py-2 text-sm group relative ${
                  isIncoming
                    ? "bg-white border border-[#e5e7eb] rounded-2xl rounded-tl-sm text-[#111827]"
                    : "bg-indigo-600 text-white rounded-2xl rounded-tr-sm"
                }`}
              >
                <p>{msg.text}</p>
                <div className="flex items-center justify-end gap-1 mt-1">
                  <span
                    className={`text-[10px] ${
                      isIncoming ? "text-[#9ca3af]" : "text-indigo-200"
                    }`}
                  >
                    {msg.time}
                    {!isIncoming && (
                      <span className="ml-1 text-[11px]">
                        {msg.read ? "✓✓" : "✓"}
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </div>
          );
        })}

        <div ref={chatEndRef} />
      </div>

      {/* Quick Reply Templates */}
      {showTemplates && (
        <div className="px-4 py-2 bg-white border-t border-[#e5e7eb] flex gap-2 overflow-x-auto">
          {quickReplies.map((tpl) => (
            <button
              key={tpl.label}
              onClick={() => handleTemplateClick(tpl.text)}
              className="flex-shrink-0 text-xs border border-[#e5e7eb] rounded-lg px-3 py-2 text-[#374151] hover:bg-indigo-50 hover:border-indigo-300 transition-colors whitespace-nowrap cursor-pointer"
            >
              {tpl.label}
            </button>
          ))}
        </div>
      )}

      {/* Input bar */}
      <div className="bg-white border-t border-[#e5e7eb]">
        <div className="px-4 py-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowTemplates(!showTemplates)}
              className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                showTemplates ? "text-indigo-600 bg-indigo-50" : "text-gray-400 hover:text-indigo-600 hover:bg-gray-100"
              }`}
            >
              <i className="ri-flashlight-line text-base" />
            </button>
            <button
              onClick={() => showToast("Attachment upload active", "info")}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-attachment-2 text-base" />
            </button>
            <button
              onClick={() => showToast("Emoji selected", "info")}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-emotion-line text-base" />
            </button>

            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={`Message to ${conversation.name} (${conversation.phone || "WhatsApp"})...`}
              className="flex-1 bg-gray-100 rounded-xl px-4 py-2 text-sm text-[#111827] placeholder-[#9ca3af] border-none focus:outline-none focus:ring-1 focus:ring-green-500"
            />

            <button
              onClick={handleSend}
              disabled={!input.trim()}
              title="Send message to lead's WhatsApp"
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-green-500 hover:bg-green-600 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 cursor-pointer shadow-sm"
            >
              <i className="ri-send-plane-fill text-base" />
            </button>
          </div>
        </div>

        {/* Bottom info: Target WhatsApp number and sync status */}
        <div className="px-4 pb-2.5 flex items-center justify-between text-xs text-[#6b7280] border-t border-gray-50 pt-1.5">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-green-700 font-medium">WhatsApp Sync Active</span>
            <span className="text-gray-300">&bull;</span>
            <span>
              Target Number: <strong className="text-gray-900">{conversation.phone || "No phone linked"}</strong>
            </span>
          </div>
          <span className="text-[11px] text-gray-500 flex items-center gap-1">
            <i className="ri-check-double-line text-green-600" /> Direct CRM delivery
          </span>
        </div>
      </div>

      {/* Agent picker dropdown */}
      {showAgentPicker && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setShowAgentPicker(false)} />
          <div className="absolute right-4 top-14 z-40 w-64 bg-white rounded-xl border border-[#e5e7eb] shadow-lg py-1 animate-slide-in">
            <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {agentPickerMode === "assign" ? "Assign to Agent" : "Reassign to Agent"}
            </p>
            {agentsList.map((agent) => (
              <button
                key={agent.id}
                onClick={() => handleAgentSelect(agent)}
                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors flex items-center gap-3 cursor-pointer"
              >
                <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold">{agent.initials}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[#111827]">{agent.name}</p>
                  <p className="text-xs text-[#9ca3af]">
                    {agent.role || "Sales Agent"}
                  </p>
                </div>
              </button>
            ))}
            <button
              onClick={() => setShowAgentPicker(false)}
              className="w-full text-left px-4 py-2 text-xs text-gray-500 hover:bg-gray-50 transition-colors border-t border-[#f1f5f9] cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </>
      )}

      {/* Reassign Modal */}
      {showReassignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl w-full max-w-sm shadow-lg animate-slide-in p-6">
            <h3 className="text-base font-semibold text-[#111827] mb-4">Reassign Chat</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Reassign to:</label>
                <select
                  value={reassignAgentId}
                  onChange={(e) => setReassignAgentId(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 cursor-pointer"
                >
                  <option value="">Select agent...</option>
                  {agentsList
                    .filter((a) => a.name !== conversation.assignedAgentName)
                    .map((a) => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Reason <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={reassignReason}
                  onChange={(e) => setReassignReason(e.target.value)}
                  placeholder="e.g. Agent on leave, client requested different agent"
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200"
                />
              </div>
            </div>

            <div className="flex items-center gap-2.5 mt-5 justify-end">
              <button
                onClick={() => setShowReassignModal(false)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleReassignConfirm}
                disabled={!reassignAgentId}
                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-sm font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                Reassign
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Call Modal */}
      <CallModal
        isOpen={callModalOpen}
        onClose={() => setCallModalOpen(false)}
        contactName={conversation.name}
        phone={conversation.phone}
        leadId={conversation.leadId}
        agentName={conversation.assigned || conversation.assignedAgentName}
      />
    </div>
  );
}